import { useLazyQuery } from "@apollo/client/react";
import { useEffect } from "react";
import { Box, Typography } from "@mui/material";
import { CustomButton } from "../../../../components/customButton/CustomButton";
import DataGridGenerico from "../../../../components/dataGrid/DataGridGenerico";
import { TextInput } from "../../../../components/textInput/TextInput";
import { GET_ACTUALIZAR_STOCK_SAP } from "../../../../data/graphQL/queries/GestionAsistencial/getActualizarStockSAP";
import { useDialog } from "../../../../hooks/useDialog";
import { useInsumoSectionColumns } from "../../hooks/useInsumoSectionColumns";
import { CustomSection } from "../CustomSection";
import { InsumosSearchDialog } from "../InsumosSearchDialog";
import { ENVIO_ESTADO_AUTORIZADO_ID } from "../../hooks/useEnvioCreationContentInputs";
import "./insumoSection.scss";

export function InsumoSection({
  insumosRows,
  setInsumosRows,
  data,
  loading,
  idEvaluacion,
  centroSAP,
  almacenSAP,
  isActualizarStockRequired,
  disabledNormalInput,
  setIsActualizarStockRequired,
  initialEnvioEstado
}) {
  const { codSAPPaciente } = data?.envio?.cabecera || {};

  const { isDialogOpen, handleOpenDialog, handleCloseDialog } = useDialog();

  const columns = useInsumoSectionColumns(setInsumosRows, isActualizarStockRequired, disabledNormalInput, initialEnvioEstado);

  const [getActualizarStockSAP, { data: dataActualizarStockSAP, loading: loadingActualizarStock }] =
    useLazyQuery(GET_ACTUALIZAR_STOCK_SAP, {
      fetchPolicy: 'no-cache'
    });

  const handleActualizarStockSAP = () => {
    const insumosFormatted = insumosRows.map(
      ({
        saldo,
        cod_Articulo,
        stockDisponibleDeposito,
        cantidadCuota,
        cuotaActiva,
        cantidadAPedir,
        cantidadDiaria,
        cantidadPlan,
        descripcion,
        idInsumo,
        idInsumoN,
        por,
        indFutura,
        ...rest
      }) => ({
        cantidadAPedir,
        cantidadDiaria,
        cantidadPlan,
        descripcion,
        idInsumo,
        idInsumoN,
        por,
        indFutura
      })
    );

    getActualizarStockSAP({
      variables: {
        codSAPPaciente,
        centroSAP,
        almacenSAP,
        insumos: insumosFormatted
      }
    });
  };

  useEffect(() => {
    if (!dataActualizarStockSAP) return;
    const stockMap = dataActualizarStockSAP.actualizarStockSAP.reduce(
      (acc, { __typename, ...item }) => {
        acc[item.cod_Articulo] = item;
        return acc;
      },
      {}
    );

    const updatedRows = insumosRows.map((insumo) => ({
      ...insumo,
      ...(stockMap[insumo.idInsumo] || {})
    }));

    setInsumosRows(updatedRows);
    setIsActualizarStockRequired(false);
  }, [dataActualizarStockSAP]);

  // Función para aplicar clases CSS según las props de la row
  const getRowClassName = (params) => {
    const classes = [];

    // Mantener el patrón de rayas (even/odd), estilo default de componente DataGrid
    if (params.indexRelativeToCurrentPage % 2 === 0) {
      classes.push('even');
    } else {
      classes.push('odd');
    }

    if (!!params?.row?.por && params?.row?.por === "I") {
      if (params?.row?.indFutura)
        classes.push('row-insumo-indicacion-futura');
      else
        classes.push('row-insumo-indicacion');
    }

    return classes.join(' ');
  };

  return (
    <>
      <CustomSection title="Detalle de insumo">
        <Box
          sx={{
            width: 1,
            display: "flex",
            justifyContent: "space-between",
            flexDirection: { lg: "row", md: "column" }
          }}>

          <Box sx={{ width: { lg: "270px", md: 2 } }}>
            <TextInput
              placeholder="Agregar nuevo insumo"
              value={""}
              searchIcon
              disabled={disabledNormalInput || initialEnvioEstado === ENVIO_ESTADO_AUTORIZADO_ID}
              onClick={handleOpenDialog} />

          </Box>
          <CustomButton
            label="Actualizar Stock SAP"
            variant="outlined"
            onClick={() => handleActualizarStockSAP()}
            disabled={disabledNormalInput}
            isLoading={loadingActualizarStock} />

        </Box>
        <DataGridGenerico
          columns={columns}
          rows={insumosRows}
          getRowClassName={getRowClassName}
          loading={loading}
          toolbar={false}
        />

        <Box>
          <div class='insumos-referencias-indicaciones'>
            <div class='row-insumo-indicacion insumo-ind-actual'>&nbsp;</div>
            <div style={{ paddingLeft: 5 }}>
              <Typography component="p" fontSize="14px">
                insumo de indicación activa
              </Typography>
            </div>
            <div class='row-insumo-indicacion-futura insumo-ind-futura'>&nbsp;</div>
            <div style={{ paddingLeft: 5 }}>
              <Typography component="p" fontSize="14px">
                insumo de indicación futura
              </Typography>
            </div>
          </div>
          <Typography component="p" fontSize="14px">
            Por: (M) Insumo del Plan. (I) Insumo de Indicación. (A) Insumo
            Adicional.
          </Typography>
          <Typography component="p" fontSize="14px">
            Stock Teórico: Valor aproximado, calculado para la indicación,
            tomando como base el último pedido.
          </Typography>
        </Box>
      </CustomSection>

      {isDialogOpen &&
        <InsumosSearchDialog
          open={isDialogOpen}
          handleCloseDialog={handleCloseDialog}
          idEvaluacion={idEvaluacion}
          insumosRows={insumosRows}
          setInsumosRows={setInsumosRows}
          setIsActualizarStockRequired={setIsActualizarStockRequired} />

      }
    </>);

}